// src/components/editform.js
import React, { useState, useRef, useEffect } from 'react';
import axios            from 'axios';
import imageCompression from 'browser-image-compression';
import { isMediaImage, isMediaVideo } from '../utils/mediaDetect';
import { hydrateMentionsFromText, toStoredMentionText } from '../utils/mentions';
import { compressVideo, formatBytes } from '../utils/compressVideo';
import UploadProgressBar from './shared/UploadProgressBar';
import MediaComposePreview, {
  REORDER_DRAG_TYPE,
} from './shared/MediaComposePreview';

const isReorderDrag = (e) =>
  Array.from(e.dataTransfer?.types || []).includes(REORDER_DRAG_TYPE);

/* ─────────────────────────────────────────────────────────────
   EditPostForm
   ───────────────────────────────────────────────────────────── */
export default function EditPostForm({ post, user, onSuccess }) {
  const safePost = post ?? {};
  const API_URL = process.env.REACT_APP_API_URL;

  const [title,     setTitle]     = useState(safePost.title          ?? '');
  const [content,   setContent]   = useState(
    hydrateMentionsFromText((safePost.content ?? safePost.text ?? '').trim())
  );
  const [category,  setCategory]  = useState(safePost.category ?? 'General');
  const [country,   setCountry]   = useState(safePost.country  ?? '');
  const [loading,   setLoading]   = useState(false);

  const resolveMediaUrl = (url) => {
    if (!url) return '';
    return /^https?:\/\//i.test(url)
      ? url
      : `${API_URL}/${String(url).replace(/^\/+/, '')}`;
  };

  const buildMediaItems = (mediaList) =>
    Array.isArray(mediaList)
      ? mediaList.map((m, i) => {
          const storedUrl = m.url || m.media_url || '';
          return {
            id: `existing-${i}-${storedUrl}`,
            kind: 'existing',
            url: resolveMediaUrl(storedUrl),
            storedUrl,
            type: m.type || m.media_type || 'image',
          };
        })
      : [];

  const [mediaItems, setMediaItems] = useState(buildMediaItems(safePost.media));
  const [isDragging, setIsDragging] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const previewUrlsRef = useRef(new Map());

  useEffect(() => {
    setMediaItems(buildMediaItems(post?.media));
  }, [post?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const urls = previewUrlsRef.current;
    const activeIds = new Set(
      mediaItems.filter((item) => item.kind === 'new').map((item) => item.id)
    );

    mediaItems.forEach((item) => {
      if (item.kind === 'new' && !urls.has(item.id)) {
        urls.set(item.id, URL.createObjectURL(item.file));
      }
    });

    for (const [id, url] of urls.entries()) {
      if (!activeIds.has(id)) {
        URL.revokeObjectURL(url);
        urls.delete(id);
      }
    }
  }, [mediaItems]);

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    []
  );

  const addFiles = (files) =>
    setMediaItems((prev) => [
      ...prev,
      ...Array.from(files).map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        kind: 'new',
        file,
      })),
    ]);

  const removeItem = (idx) =>
    setMediaItems((prev) => prev.filter((_, i) => i !== idx));

  const reorderItems = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setMediaItems((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const moveItem = (idx, direction) => reorderItems(idx, idx + direction);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      alert('Not logged in');
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setSubmitStatus('Preparing…');
    try {
      const formData = new FormData();
      formData.append('post_id', safePost.id);
      formData.append('title',    title.trim());
      formData.append('content',  toStoredMentionText(content.trim()));
      formData.append('category', category);
      formData.append('country',  country);
      if (user?.id) formData.append('user_id', user.id);

      const newItems = mediaItems.filter((item) => item.kind === 'new');
      let newIndex = 0;
      const mediaOrder = mediaItems.map((item) => {
        if (item.kind === 'existing') {
          return { existing: item.storedUrl || item.url };
        }
        return { new: newIndex++ };
      });

      formData.append(
        'keep_media',
        JSON.stringify(
          mediaItems
            .filter((item) => item.kind === 'existing')
            .map((item) => item.storedUrl || item.url)
        )
      );
      formData.append('media_order', JSON.stringify(mediaOrder));

      const totalMedia = newItems.length;
      for (let i = 0; i < newItems.length; i++) {
        const f = newItems[i].file;
        const prepPct = Math.round(((i + 0.5) / Math.max(totalMedia, 1)) * 40);

        if (f.type.startsWith('image/')) {
          setSubmitStatus(
            totalMedia > 1
              ? `Compressing photo ${i + 1} of ${totalMedia}…`
              : 'Compressing photo…'
          );
          setUploadProgress(prepPct);
          try {
            const img = await imageCompression(f, {
              maxSizeMB: 1.5,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              onProgress: (pct) => {
                setSubmitStatus(
                  totalMedia > 1
                    ? `Compressing photo ${i + 1} of ${totalMedia}… ${pct}%`
                    : `Compressing photo… ${pct}%`
                );
                setUploadProgress(
                  Math.min(
                    39,
                    prepPct + Math.round((pct / 100) * (40 / Math.max(totalMedia, 1)))
                  )
                );
              },
            });
            formData.append('media[]', img, img.name);
          } catch {
            formData.append('media[]', f);
          }
        } else if (
          f.type.startsWith('video/') ||
          /\.(mp4|m4v|mov|webm|ogg|ogv|avi|mkv|3gp)$/i.test(f.name)
        ) {
          try {
            const originalSize = formatBytes(f.size);
            setSubmitStatus(
              totalMedia > 1
                ? `Compressing video ${i + 1} of ${totalMedia} (${originalSize})…`
                : `Compressing video (${originalSize})…`
            );
            setUploadProgress(prepPct);
            const result = await compressVideo(f, {
              onProgress: (pct) => {
                setSubmitStatus(
                  totalMedia > 1
                    ? `Compressing video ${i + 1} of ${totalMedia}… ${pct}%`
                    : `Compressing video… ${pct}%`
                );
                setUploadProgress(
                  Math.min(
                    39,
                    prepPct + Math.round((pct / 100) * (40 / Math.max(totalMedia, 1)))
                  )
                );
              },
            });
            const compressedVideo = result.file || result;
            if (result.mode === 'server') {
              setSubmitStatus(
                result.message ||
                  `Uploading ${originalSize} — server will convert…`
              );
            } else if (result.mode === 'compressed') {
              setSubmitStatus(
                `Compressed ${originalSize} → ${formatBytes(compressedVideo.size)}`
              );
            }
            formData.append('media[]', compressedVideo, compressedVideo.name);
          } catch (err) {
            console.warn('Video prep failed:', err);
            throw new Error(
              err?.message ||
                `Could not prepare "${f.name}" for upload. Use a smaller H.264 MP4 (under 80MB).`
            );
          }
        }
      }

      setSubmitStatus(totalMedia > 0 ? 'Uploading media…' : 'Saving…');
      setUploadProgress(totalMedia > 0 ? 40 : 10);

      const { data } = await axios.post(
        `${API_URL}/update_post.php`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 0,
          onUploadProgress: (event) => {
            const total = event.total || event.loaded;
            if (!total) return;
            const ratio = Math.min(1, event.loaded / total);
            setUploadProgress(Math.min(100, 40 + Math.round(ratio * 60)));
            const pct = Math.round(ratio * 100);
            setSubmitStatus(
              totalMedia > 0 ? `Uploading media… ${pct}%` : `Saving… ${pct}%`
            );
          },
        }
      );

      setUploadProgress(100);
      setSubmitStatus('Finishing…');

      if (data.success) {
        alert('Post updated!');
        onSuccess?.();
      } else {
        alert(data.message || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setSubmitStatus('');
      setUploadProgress(0);
    }
  };

  if (!post) {
    return <p style={{ padding: 20 }}>Select a post to edit.</p>;
  }

  return (
    <form onSubmit={handleSave} style={styles.wrapper}>
      <h3>Edit Post</h3>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        required
        style={styles.input}
      />

      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="What's on your mind?"
        required
        style={{ ...styles.input, minHeight: 100 }}
      />

      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        style={styles.input}
      >
        {['General', 'Really Lesson', 'COOL', 'ReallyAvatar', 'Coursera', 'TechTree']
          .map((c) => (
            <option key={c}>{c}</option>
        ))}
      </select>

      <DropZone
        isDragging={isDragging}
        setIsDragging={setIsDragging}
        onFiles={addFiles}
      />

      {mediaItems.length > 0 && (
        <MediaComposePreview
          items={mediaItems.map((item) => {
            const url =
              item.kind === 'existing'
                ? item.url
                : previewUrlsRef.current.get(item.id);
            const isVideo =
              item.kind === 'new'
                ? Boolean(item.file?.type?.startsWith('video/'))
                : isMediaVideo(item.url || item.storedUrl, item.type);
            return { id: item.id, url, isVideo };
          })}
          onRemove={removeItem}
          onReorder={reorderItems}
          onMove={moveItem}
        />
      )}

      {loading && (
        <UploadProgressBar status={submitStatus} progress={uploadProgress} />
      )}

      <button type="submit" disabled={loading} style={styles.saveBtn}>
        {loading
          ? uploadProgress > 0
            ? `Uploading ${uploadProgress}%`
            : submitStatus || 'Saving…'
          : 'Save Changes'}
      </button>
    </form>
  );
}

function DropZone({ isDragging, setIsDragging, onFiles }) {
  return (
    <div
      onClick={() => document.getElementById('editMediaInput').click()}
      onDrop={(e) => {
        if (isReorderDrag(e)) {
          setIsDragging(false);
          return;
        }
        e.preventDefault();
        setIsDragging(false);
        onFiles(Array.from(e.dataTransfer.files));
      }}
      onDragOver={(e) => {
        if (isReorderDrag(e)) return;
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        if (isReorderDrag(e)) return;
        setIsDragging(false);
      }}
      style={{
        border: isDragging ? '2px dashed #0077b6' : '2px dashed #ccc',
        padding: 20,
        marginBottom: 12,
        textAlign: 'center',
        background: isDragging ? '#e0f7fa' : '#f9f9f9',
        cursor: 'pointer',
      }}
    >
      Drag & drop photos and videos or <strong>click to add</strong>
      <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
        You can mix photos and videos, then arrange the order below.
      </div>
      <input
        id="editMediaInput"
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => onFiles(Array.from(e.target.files))}
      />
    </div>
  );
}

const styles = {
  wrapper: {
    maxWidth: 600,
    background: '#fff',
    padding: 20,
    borderRadius: 8,
  },
  input: {
    width: '100%',
    padding: 10,
    fontSize: 16,
    borderRadius: 5,
    border: '1px solid #ccc',
    marginBottom: 12,
  },
  saveBtn: {
    background: '#0091c2',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: 5,
    width: '100%',
    fontSize: 16,
    cursor: 'pointer',
  },
};
